// QueryClientProvider с сохранением кэша в IndexedDB и фоновой отправкой офлайн-очереди.

import type { QueryClient, Query } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react'
import { getIdentityKey, subscribeIdentity } from '@/api/identity'

import { CACHE_BUSTER, CACHE_MAX_AGE_MS, configureOfflineDefaults, createIdbPersister } from './persister'
import { startQueueSync } from './sync'

const shouldDehydrateQuery = (query: Query) => query.state.status === 'success'

export function OfflineQueryProvider({ client, children }: { client: QueryClient; children: ReactNode }) {
  const owner = useSyncExternalStore(subscribeIdentity, getIdentityKey, getIdentityKey)
  useState(() => {
    configureOfflineDefaults(client)
    return null
  })
  const persister = useMemo(() => { void owner; return createIdbPersister() }, [owner])
  useEffect(() => startQueueSync(client), [client, owner])
  return (
    <PersistQueryClientProvider
      key={owner ?? 'anonymous'}
      client={client}
      persistOptions={{
        persister,
        maxAge: CACHE_MAX_AGE_MS,
        buster: CACHE_BUSTER,
        dehydrateOptions: { shouldDehydrateQuery },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  )
}
